import { LockReset } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, TextField, Typography } from "@mui/material";
import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fieldProps, useValidatedForm } from "../../../library/formValidation";
import { getVisibilityButton } from "../../../library/formsHelper";
import { useApiFormError } from "../../../library/useApiFormError";
import ResourcesIds from "../../../resources/ResourcesIds";
import { useReactMutation, useReactQuery } from "../../../services/queryService";
import { services } from "../../../services/services";
import { ChangePasswordRequest } from "../../../types";
import { cardContentStyles, cardStyles, pageWrapperStyles, passwordFieldStyles, signInButtonStyles } from "../../auth/loginForm.styles";
import { headerBoxStyles } from "../Dashboard/dashboard.styles";
import { createChangePasswordSchema } from "./changePasswordSchema";

interface ChangePasswordRequestData extends ChangePasswordRequest {
    confirmPassword: string;
}

export const ChangePassowrd = (): JSX.Element => {
    const { key } = useParams();
    const [showPassword, setShowPassword] = React.useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState<boolean>(false);
    const [isPasswordChanged, setIsPasswordChanged] = React.useState<boolean>(false);

    const { t: translate } = useTranslation();

    const schema = React.useMemo(() => createChangePasswordSchema(translate), [translate]);

    const {
        register, handleSubmit, setError, formState: { errors, isValid },
    } = useValidatedForm<ChangePasswordRequestData>(schema, {
        defaultValues: { newPassword: '', confirmPassword: '' },
    });

    const knownFields: string[] = React.useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError: changePasswordError, report, clear } =
        useApiFormError<ChangePasswordRequestData>(
            setError, knownFields, translate, ResourcesIds.CHANGE_PASSWORD_ERROR);
    const { data, isLoading, error } = useReactQuery(services.authService.verifyChangePasswordCode(key));

    const changePasswordMutation = useReactMutation<void, Error, ChangePasswordRequest>(services.authService.changePasswordMutation(key!));

    const onSubmit = (values: ChangePasswordRequestData): void => {
        clear();

        changePasswordMutation.mutate({ newPassword: values.newPassword }, {
            onSuccess: () => setIsPasswordChanged(true),
            onError: report,
        });
    };

    if (isLoading)
        return <></>

    return (
        <Box sx={pageWrapperStyles}>
            <Card sx={cardStyles}>
                <CardContent sx={cardContentStyles}>
                    {data && (
                        <>
                            {isPasswordChanged ?
                                <Box sx={headerBoxStyles}>
                                    <Typography variant="h4" component="h1" gutterBottom>
                                        {translate(ResourcesIds.CHANGE_PASSWORD_SUCCESS_TITLE)}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {translate(ResourcesIds.CHANGE_PASSWORD_SUCCESS_SUBTITLE)}
                                    </Typography>
                                </Box>
                                :
                                <>
                                    <Box sx={headerBoxStyles}>
                                        <Typography variant="h4" component="h1" gutterBottom>
                                            {translate(ResourcesIds.CHANGE_PASSWORD_TITLE)}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {translate(ResourcesIds.CHANGE_PASSWORD_SUBTITLE)}
                                        </Typography>
                                    </Box>

                                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
                                    {changePasswordError && <Alert severity="error" sx={{ mb: 2 }}>{changePasswordError}</Alert>}

                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <TextField
                                            {...register('newPassword')}
                                            fullWidth
                                            label={translate(ResourcesIds.PASSWORD_LABEL)}
                                            type={showPassword ? 'text' : 'password'}
                                            {...fieldProps(errors, 'newPassword')}
                                            margin="normal"
                                            slotProps={{ input: { endAdornment: (getVisibilityButton(showPassword, () => setShowPassword(!showPassword))), } }}
                                            sx={passwordFieldStyles}
                                        />

                                        <TextField
                                            fullWidth
                                            label={translate(ResourcesIds.CONFIRM_PASSWORD_LABEL)}
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            margin="normal"
                                            slotProps={{ input: { endAdornment: (getVisibilityButton(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword))), } }}
                                            {...register('confirmPassword')}
                                            {...fieldProps(errors, 'confirmPassword')}
                                        />

                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={!isValid || isLoading || changePasswordMutation.isPending}
                                            startIcon={<LockReset />}
                                            sx={signInButtonStyles}
                                        >
                                            {translate(ResourcesIds.CHANGE_PASSWORD_SUBMIT)}
                                        </Button>
                                    </form>
                                </>
                            }
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}